/**
 * IndexedDB offline command outbox — command_id + payload_hash; ordered replay.
 * Pending is never treated as accepted and never mutates displayed stock.
 */

const DB_NAME = 'foodops-scan-outbox';
const STORE = 'commands';
const FINAL_HTTP = new Set([400, 403, 404, 409, 410, 422]);

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'command_id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function enqueue(command) {
  if (!command?.command_id || !command?.payload_hash) {
    throw new Error('command_id and payload_hash required');
  }
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put({
      ...command,
      status: 'pending',
      attempts: 0,
      enqueued_at: new Date().toISOString(),
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function listPending() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).getAll();
    req.onsuccess = () => {
      const rows = (req.result || [])
        .filter((r) => r.status === 'pending' || r.status === 'conflict')
        .sort((a, b) => String(a.enqueued_at).localeCompare(String(b.enqueued_at)));
      resolve(rows);
    };
    req.onerror = () => reject(req.error);
  });
}

export function displayStatus(row) {
  if (!row) return null;
  if (row.status === 'pending') return 'PENDING';
  if (row.status === 'accepted') return 'accepted';
  if (row.status === 'conflict') return 'conflict';
  return row.status;
}

/** Pending must never render as accepted. */
export function isDisplayedAsAccepted(row) {
  return displayStatus(row) === 'accepted';
}

export async function replayAll(sendFn, { maxAttempts = 5 } = {}) {
  const pending = await listPending();
  const results = [];
  for (const row of pending) {
    if (row.attempts >= maxAttempts) {
      results.push({ command_id: row.command_id, status: 'conflict', reason: 'max_attempts' });
      continue;
    }
    try {
      const res = await sendFn(row);
      const status = res?.status ?? res?.httpStatus;
      if (status === 401) {
        results.push({ command_id: row.command_id, status: 'reauth' });
        break;
      }
      if (FINAL_HTTP.has(status)) {
        await _setStatus(row.command_id, 'conflict', { httpStatus: status });
        results.push({ command_id: row.command_id, status: 'conflict', httpStatus: status });
        continue;
      }
      if (status >= 200 && status < 300) {
        await _setStatus(row.command_id, 'accepted', { server: res.body });
        results.push({ command_id: row.command_id, status: 'accepted' });
        continue;
      }
      await _bumpAttempt(row.command_id);
      results.push({ command_id: row.command_id, status: 'backoff' });
    } catch {
      await _bumpAttempt(row.command_id);
      results.push({ command_id: row.command_id, status: 'backoff' });
    }
  }
  return results;
}

async function _setStatus(command_id, status, extra = {}) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const get = store.get(command_id);
    get.onsuccess = () => {
      const row = get.result;
      if (!row) return;
      store.put({ ...row, status, ...extra });
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function _bumpAttempt(command_id) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const get = store.get(command_id);
    get.onsuccess = () => {
      const row = get.result;
      if (!row) return;
      store.put({ ...row, attempts: (row.attempts || 0) + 1 });
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
